import DOMPurify from "dompurify";
import { supabase } from "../config/supabase";
import { NewPatientForm } from "../types/supabase";
import {
  normalizePatientEmail,
  normalizePatientPhone,
  normalizePatientCpf,
  normalizePatientRg,
  validatePatientEmail,
  validatePatientDocuments,
  validatePatientPhone,
} from "../utils/patientRegistration";

interface CreatePatientRegistrationParams {
  clientId: string;
  formData: NewPatientForm;
}

export interface CreatePatientRegistrationResult {
  patientId: string;
  action: "created" | "linked" | "already_linked";
}

interface ExistingPatientRow {
  patientId: string;
  userId?: string | null;
  patientCpf?: string | null;
  patientRg?: string | null;
  patientEmail?: string | null;
  patientPhone?: string | null;
}

const sanitizeList = (value: string): string[] =>
  value
    .split(",")
    .map((item) => DOMPurify.sanitize(item.trim()))
    .filter(Boolean);

const normalizeCpf = (cpf?: string): string => (cpf ?? "").replace(/\D/g, "");

const formatCpf = (cpf: string): string => {
  const normalizedCpf = normalizeCpf(cpf);

  if (normalizedCpf.length !== 11) {
    return cpf.trim();
  }

  return normalizedCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const getCpfLookupVariants = (cpf: string): string[] => {
  const trimmedCpf = cpf.trim();
  const normalizedCpf = normalizeCpf(cpf);
  const formattedCpf = formatCpf(cpf);

  return [...new Set([trimmedCpf, normalizedCpf, formattedCpf].filter(Boolean))];
};

const normalizeRgForLookup = (rg?: string): string => {
  return (rg ?? "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();
};

const getRgLookupVariants = (rg: string): string[] => {
  const trimmedRg = rg.trim();
  const upperRg = trimmedRg.toUpperCase();
  const normalizedRg = normalizeRgForLookup(rg);

  return [...new Set([trimmedRg, upperRg, normalizedRg].filter(Boolean))];
};

const isMissingPatientRgColumn = (error: unknown): error is { code?: string; message?: string } => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const err = error as { code?: string; message?: string };
  return err.code === "42703" && typeof err.message === "string" && err.message.includes("patientRg");
};

const stripUnsupportedPatientRg = <T extends Record<string, any>>(payload: T): Omit<T, "patientRg"> => {
  const { patientRg, ...payloadWithoutRg } = payload;
  return payloadWithoutRg;
};

const getExistingPatientSelect = (includeRg: boolean): string => {
  const rgField = includeRg ? "patientRg," : "";

  return `patientId, userId, patientCpf, ${rgField} patientEmail, patientPhone`;
};

const findExistingPatientByCpf = async (cpf: string): Promise<ExistingPatientRow | null> => {
  const cpfVariants = getCpfLookupVariants(cpf);

  if (cpfVariants.length === 0) {
    return null;
  }

  let { data, error } = await supabase
    .from("patients")
    .select(getExistingPatientSelect(true))
    .in("patientCpf", cpfVariants);

  if (isMissingPatientRgColumn(error)) {
    ({ data, error } = await supabase
      .from("patients")
      .select(getExistingPatientSelect(false))
      .in("patientCpf", cpfVariants));
  }

  if (error) {
    throw error;
  }

  if (!data || data.length === 0) {
    return null;
  }

  const normalizedCpf = normalizeCpf(cpf);

  return (
    data.find((patient) => normalizeCpf(patient.patientCpf) === normalizedCpf) ??
    data[0] ??
    null
  );
};

const findExistingPatientByRg = async (rg: string): Promise<ExistingPatientRow | null> => {
  const rgVariants = getRgLookupVariants(rg);

  if (rgVariants.length === 0) {
    return null;
  }

  let { data, error } = await supabase
    .from("patients")
    .select(getExistingPatientSelect(true))
    .in("patientRg", rgVariants);

  if (isMissingPatientRgColumn(error)) {
    return null;
  }

  if (error) {
    throw error;
  }

  if (!data || data.length === 0) {
    return null;
  }

  const normalizedRg = normalizeRgForLookup(rg);

  return (
    data.find((patient) => normalizeRgForLookup(patient.patientRg) === normalizedRg) ??
    data[0] ??
    null
  );
};

const findExistingPatientByDocument = async ({
  cpf,
  rg,
}: {
  cpf?: string | null;
  rg?: string | null;
}): Promise<ExistingPatientRow | null> => {
  if (cpf) {
    const patientByCpf = await findExistingPatientByCpf(cpf);
    if (patientByCpf) {
      return patientByCpf;
    }
  }

  if (rg) {
    return await findExistingPatientByRg(rg);
  }

  return null;
};

const hasExistingValue = (value?: string | null): boolean => {
  return Boolean(value?.trim());
};

const updateExistingPatientContactIfEmpty = async ({
  patient,
  patientEmail,
  patientPhone,
  nowIso,
}: {
  patient: ExistingPatientRow;
  patientEmail: string | null;
  patientPhone: string | null;
  nowIso: string;
}): Promise<void> => {
  const payload: {
    patientEmail?: string | null;
    patientPhone?: string | null;
    patientUpdatedAt?: string;
  } = {};

  if (!hasExistingValue(patient.patientEmail) && patientEmail) {
    payload.patientEmail = patientEmail;
  }

  if (!hasExistingValue(patient.patientPhone) && patientPhone) {
    payload.patientPhone = patientPhone;
  }

  if (Object.keys(payload).length === 0) {
    return;
  }

  payload.patientUpdatedAt = nowIso;

  const { error } = await supabase
    .from("patients")
    .update(payload)
    .eq("patientId", patient.patientId);

  if (error) {
    throw error;
  }
};

const ensurePatientUserClientLink = async (
  userId: string | null | undefined,
  clientId: string,
  nowIso: string
): Promise<"linked" | "already_linked" | "no_user"> => {
  if (!userId) {
    return "no_user";
  }

  const { data: existingLink, error: userClientLookupError } = await supabase
    .from("user_clients")
    .select("userId")
    .eq("userId", userId)
    .eq("clientId", clientId)
    .maybeSingle();

  if (userClientLookupError) {
    throw userClientLookupError;
  }

  if (existingLink) {
    return "already_linked";
  }

  const { error: userClientInsertError } = await supabase.from("user_clients").insert({
    userId,
    clientId,
    userRole: "patient",
    createdAt: nowIso,
  });

  if (userClientInsertError) {
    if (userClientInsertError.code === "23505") {
      return "already_linked";
    }

    throw userClientInsertError;
  }

  return "linked";
};

const ensurePatientClientLink = async (
  patientId: string,
  clientId: string,
  nowIso: string
): Promise<"linked" | "already_linked"> => {
  const { data: existingLink, error: patientClientLookupError } = await supabase
    .from("patient_clients")
    .select("patientId")
    .eq("patientId", patientId)
    .eq("clientId", clientId)
    .maybeSingle();

  if (patientClientLookupError) {
    throw patientClientLookupError;
  }

  if (existingLink) {
    return "already_linked";
  }

  const { error: patientClientInsertError } = await supabase.from("patient_clients").insert([
    {
      patientId,
      clientId,
      createdAt: nowIso,
    },
  ]);

  if (patientClientInsertError) {
    if (patientClientInsertError.code === "23505") {
      return "already_linked";
    }

    throw patientClientInsertError;
  }

  return "linked";
};

const ensureDefaultFinancialProfile = async (
  patientId: string,
  clientId: string,
  nowIso: string
): Promise<void> => {
  const { data: existingFinancialProfile, error: financialProfileLookupError } = await supabase
    .from("financial_profiles")
    .select("financialProfileId")
    .eq("patientId", patientId)
    .eq("clientId", clientId)
    .limit(1)
    .maybeSingle();

  if (financialProfileLookupError) {
    throw financialProfileLookupError;
  }

  if (existingFinancialProfile) {
    return;
  }

  const { error: financialProfileError } = await supabase.from("financial_profiles").insert({
    patientId,
    clientId,
    financialProfileIsActive: true,
    financialProfileHasPackage: false,
    financialProfileIndividualSessionPrice: 150,
    financialProfileRenewalStatus: "active",
    financialProfileCreatedAt: nowIso,
    financialProfileUpdatedAt: nowIso,
  });

  if (financialProfileError && financialProfileError.code !== "23505") {
    throw financialProfileError;
  }
};

const ensureAutomaticSelfPayerContract = async (
  patientId: string,
  clientId: string
): Promise<void> => {
  const { error } = await supabase.rpc("financial_a16_ensure_self_payer_contract", {
    p_client_id: clientId,
    p_patient_id: patientId,
  });

  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") {
      throw new Error("A automação financeira do cadastro ainda não foi aplicada. Execute o arquivo 35a no Supabase.");
    }

    throw error;
  }
};

const rollbackExistingPatientAttach = async ({
  clientId,
  patientId,
  userId,
  rollbackPatientClientLink,
  rollbackUserClientLink,
}: {
  clientId: string;
  patientId: string;
  userId?: string | null;
  rollbackPatientClientLink: boolean;
  rollbackUserClientLink: boolean;
}) => {
  const rollbackTasks: Promise<any>[] = [];

  if (rollbackPatientClientLink) {
    rollbackTasks.push(
      supabase.from("patient_clients").delete().eq("patientId", patientId).eq("clientId", clientId)
    );
  }

  if (rollbackUserClientLink && userId) {
    rollbackTasks.push(
      supabase.from("user_clients").delete().eq("userId", userId).eq("clientId", clientId)
    );
  }

  const rollbackResults = await Promise.all(rollbackTasks);

  rollbackResults.forEach((result, index) => {
    if (result?.error) {
      const target = index === 0 && rollbackPatientClientLink ? "patient_clients" : "user_clients";
      console.error(`Erro ao desfazer vínculo em ${target}:`, result.error);
    }
  });
};

export const isDuplicateCpfError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const err = error as {
    code?: string;
    message?: string;
    details?: string;
    constraint?: string;
  };

  const normalizedMessage = `${err.message ?? ""} ${err.details ?? ""} ${err.constraint ?? ""}`.toLowerCase();
  const isUniqueViolation = err.code === "23505" || normalizedMessage.includes("duplicate key");
  const isCpfConstraint =
    normalizedMessage.includes("patientcpf") ||
    normalizedMessage.includes("patients_patientcpf_key") ||
    normalizedMessage.includes("cpf");

  return isUniqueViolation && isCpfConstraint;
};

export const createPatientRegistration = async ({
  clientId,
  formData,
}: CreatePatientRegistrationParams): Promise<CreatePatientRegistrationResult> => {
  const documentsValidationError = validatePatientDocuments(formData.patientCpf, formData.patientRg);

  if (documentsValidationError) {
    throw new Error(documentsValidationError);
  }

  const nowIso = new Date().toISOString();
  const sanitizedConditions = DOMPurify.sanitize(formData.diagn_x00f3_sticos).trim();
  const patientCpf = normalizePatientCpf(DOMPurify.sanitize(formData.patientCpf ?? ""));
  const patientRg = normalizePatientRg(DOMPurify.sanitize(formData.patientRg ?? ""));
  const patientEmail = normalizePatientEmail(DOMPurify.sanitize(formData.patientEmail ?? ""));
  const patientPhone = normalizePatientPhone(DOMPurify.sanitize(formData.patientPhone ?? ""));
  const patientEmailValidationError = validatePatientEmail(patientEmail ?? "");
  const patientPhoneValidationError = validatePatientPhone(patientPhone ?? "");

  if (patientEmailValidationError) {
    throw new Error(patientEmailValidationError);
  }

  if (patientPhoneValidationError) {
    throw new Error(patientPhoneValidationError);
  }

  const existingPatient = await findExistingPatientByDocument({
    cpf: patientCpf,
    rg: patientRg,
  });

  if (existingPatient) {
    const userClientLinkStatus = await ensurePatientUserClientLink(existingPatient.userId, clientId, nowIso);
    let patientClientLinkStatus: "linked" | "already_linked" = "already_linked";

    try {
      patientClientLinkStatus = await ensurePatientClientLink(existingPatient.patientId, clientId, nowIso);
      await ensureDefaultFinancialProfile(existingPatient.patientId, clientId, nowIso);
      await ensureAutomaticSelfPayerContract(existingPatient.patientId, clientId);
    } catch (error) {
      await rollbackExistingPatientAttach({
        clientId,
        patientId: existingPatient.patientId,
        userId: existingPatient.userId,
        rollbackPatientClientLink: patientClientLinkStatus === "linked",
        rollbackUserClientLink: userClientLinkStatus === "linked",
      });
      throw error;
    }

    return {
      patientId: existingPatient.patientId,
      action: patientClientLinkStatus === "linked" ? "linked" : "already_linked",
    };
  }

  const patientData = {
    patientCpf,
    patientRg,
    patientFullName: `${formData.nome.trim()} ${formData.sobrenome.trim()}`,
    patientDateOfBirth: formData.data_x0020_de_x0020_nascimento,
    patientGender: formData.sexo === "M" ? "Masculino" : formData.sexo === "F" ? "Feminino" : "Outro",
    patientEmail,
    patientPhone,
    patientHeightCm: Number(formData.altura_x0020__x0028_cm_x0029_),
    patientWeightKg: Number(formData.peso),
    patientMedicalInfoConditions: sanitizedConditions ? [sanitizedConditions] : [],
    patientCreatedAt: nowIso,
    patientUpdatedAt: nowIso,
    patientAddressStreet: formData.patientAddressStreet,
    patientAddressNumber: formData.patientAddressNumber,
    patientAddressComplement: formData.patientAddressComplement,
    patientAddressNeighborhood: formData.patientAddressNeighborhood,
    patientAddressCity: formData.patientAddressCity,
    patientAddressState: formData.patientAddressState,
    patientAddressZipCode: formData.patientAddressZipCode,
    patientEmergencyContactName: formData.patientEmergencyContactName,
    patientEmergencyContactPhone: formData.patientEmergencyContactPhone,
    patientEmergencyContactRelationship: formData.patientEmergencyContactRelationship,
    patientMedicalInfoBloodType: formData.patientMedicalInfoBloodType,
    patientMedicalInfoAllergies: sanitizeList(formData.patientMedicalInfoAllergies),
    patientMedicalInfoMedications: sanitizeList(formData.patientMedicalInfoMedications),
  };

  const insertPatient = async (includeRg: boolean) => {
    const payload = includeRg ? patientData : stripUnsupportedPatientRg(patientData);

    return await supabase
      .from("patients")
      .insert([payload])
      .select("patientId")
      .single();
  };

  let { data: createdPatient, error: patientError } = await insertPatient(true);

  if (isMissingPatientRgColumn(patientError)) {
    console.warn('⚠️ Coluna patients.patientRg não encontrada no schema atual. Criando paciente sem persistir RG.');
    ({ data: createdPatient, error: patientError } = await insertPatient(false));
  }

  if (patientError) {
    throw patientError;
  }

  const patientId = createdPatient.patientId;

  const rollbackCreatedPatient = async () => {
    const { error: rollbackFinancialProfileError } = await supabase
      .from("financial_profiles")
      .delete()
      .eq("patientId", patientId)
      .eq("clientId", clientId);

    const { error: rollbackPatientClientError } = await supabase
      .from("patient_clients")
      .delete()
      .eq("patientId", patientId)
      .eq("clientId", clientId);

    const { error: rollbackPatientError } = await supabase
      .from("patients")
      .delete()
      .eq("patientId", patientId);

    if (rollbackFinancialProfileError) {
      console.error("Erro ao desfazer perfil financeiro:", rollbackFinancialProfileError);
    }

    if (rollbackPatientClientError) {
      console.error("Erro ao desfazer vínculo patient_clients:", rollbackPatientClientError);
    }

    if (rollbackPatientError) {
      console.error("Erro ao desfazer criação de paciente:", rollbackPatientError);
    }
  };

  const { error: patientClientError } = await supabase.from("patient_clients").insert([
    {
      patientId,
      clientId,
      createdAt: nowIso,
    },
  ]);

  if (patientClientError) {
    await rollbackCreatedPatient();
    throw patientClientError;
  }

  try {
    await ensureDefaultFinancialProfile(patientId, clientId, nowIso);
    await ensureAutomaticSelfPayerContract(patientId, clientId);
  } catch (error) {
    await rollbackCreatedPatient();
    throw error;
  }

  return {
    patientId,
    action: "created",
  };
};
